/**
 * Coach Panel — pure helpers (G4D CP2).
 *
 * All state derivation and message-shape mapping lives here so the
 * JSX component in `CoachPanel.jsx` stays thin and rendering-focused.
 * These helpers have no React dependency and are unit-tested from
 * `__tests__/coach_panel.test.js`.
 */

import {
  COACH_INPUT_MAX_CHARS,
  COACH_INPUT_WARN_AT,
  COACH_COUNTER_TOTAL,
  COACH_KILL_SWITCH_NOTICE,
} from "./coach_copy";

// Prompt-version marker emitted by the backend on any safe-fallback
// path (kill_switch / api_error / schema_fail / banned_phrase /
// low_confidence). Must stay in lock-step with
// `backend/modules/proposals/prompts/coach_v1.py::COACH_V1_FALLBACK_PROMPT_VERSION`.
export const COACH_FALLBACK_PROMPT_VERSION = "coach_v1_fallback";
export const COACH_PROMPT_VERSION = "coach_v1";

/**
 * Panel state derivation.
 *
 * Inputs:
 *   proposal.sections           — dict keyed by section_key
 *   conversation.messages       — array (may be empty)
 *   conversation.remaining_free_messages — int | null (null = premium)
 *   isLoading                   — boolean, first fetch in flight
 *   loadError                   — string | null
 *
 * Output (one of):
 *   "loading" | "load_error" | "locked" | "capped" | "empty" | "active"
 *
 * `load_error` overrides everything (users can't chat without state).
 * `locked` overrides remaining states (proposal has zero passed sections).
 * `capped` fires when a Free-tier caller has zero remaining messages
 *     AND at least one message has been persisted — a free caller
 *     with `remaining_free_messages === 0` and no history yet still
 *     sees the empty state (unreachable in practice, but the
 *     derivation is stable).
 */
export function derivePanelState({
  proposal,
  conversation,
  isLoading,
  loadError,
}) {
  if (isLoading) return "loading";
  if (loadError) return "load_error";
  if (!hasGeneratedSection(proposal)) return "locked";
  const remaining = conversation?.remaining_free_messages;
  const messages = conversation?.messages || [];
  // Cap-reached only makes sense once the free counter has been
  // consumed at least once. If remaining=0 but there are zero
  // messages, we treat as `empty` — the derivation stays safe if
  // the backend ever returns 0 for a fresh conversation.
  if (typeof remaining === "number" && remaining <= 0 && messages.length > 0) {
    return "capped";
  }
  if (messages.length === 0) return "empty";
  return "active";
}

/** True when the proposal has ≥1 dict-shaped section with
 * `status === "passed"`. Mirrors backend
 * `coach._proposal_has_generated_section` (post-normalisation). */
export function hasGeneratedSection(proposal) {
  const sections = proposal?.sections;
  if (!sections || typeof sections !== "object") return false;
  for (const entry of Object.values(sections)) {
    if (entry && typeof entry === "object" && entry.status === "passed") {
      return true;
    }
    // Legacy string-shaped sections — treat non-empty strings as
    // generated content (matches the backend's `normalize_sections`
    // coercion where string content becomes `status="passed"`).
    if (typeof entry === "string" && entry.trim().length > 0) {
      return true;
    }
  }
  return false;
}

/** True for Free callers. Premium sees `remaining_free_messages=null`. */
export function isFreeTierConversation(conversation) {
  return typeof conversation?.remaining_free_messages === "number";
}

/**
 * Character-limit UX helpers.
 * - `charLimitState(len)` returns one of "ok" | "warn" | "over".
 * - "warn" at >= 1800 chars.
 * - "over" at > 2000 chars — send button MUST disable.
 */
export function charLimitState(len) {
  if (len > COACH_INPUT_MAX_CHARS) return "over";
  if (len >= COACH_INPUT_WARN_AT) return "warn";
  return "ok";
}

/** True when the panel should classify a coach reply as a fallback
 * (safe-fallback envelope). The backend stamps
 * `prompt_version = "coach_v1_fallback"` on every failure path. */
export function isFallbackReply(coachMessage) {
  if (!coachMessage) return false;
  if (coachMessage.role !== "coach") return false;
  return coachMessage.prompt_version === COACH_FALLBACK_PROMPT_VERSION;
}

/** Sanitise an action title into a follow-up user-message payload.
 * The Ask Follow-up button uses this: it sends the action's title
 * verbatim (trimmed, trailing punctuation stripped) as the next
 * user message. */
export function askFollowupPayload(action) {
  const raw = String(action?.title || "").trim();
  // Strip trailing punctuation of the "?", ".", "!", "…" family so the
  // sent message reads as a plain assertion the LLM sees as new
  // input, not a re-echo of its own question.
  const cleaned = raw.replace(/[?.!…]+$/u, "").trim();
  return cleaned || raw;
}

/** Convert a coach message dict returned by the API into the shape
 * the render layer expects — normalises optional keys and coerces
 * dates into JS Date objects. */
export function normalizeCoachMessage(msg) {
  return {
    id: msg?.id,
    role: msg?.role,
    content: msg?.content || "",
    recommended_actions: Array.isArray(msg?.recommended_actions)
      ? msg.recommended_actions
      : null,
    prompt_version: msg?.prompt_version || null,
    llm_failure_reason: msg?.llm_failure_reason || null,
    confidence:
      typeof msg?.confidence === "number" ? msg.confidence : null,
    created_at: msg?.created_at ? new Date(msg.created_at) : null,
  };
}

/** Return the neutral kill-switch / fallback banner text when the
 * message is a fallback envelope. Empty string otherwise. Used by
 * the render layer to conditionally show `[data-testid="coach-kill-switch-notice"]`. */
export function fallbackBannerFor(coachMessage) {
  return isFallbackReply(coachMessage) ? COACH_KILL_SWITCH_NOTICE : "";
}

/** Total free-tier cap constant re-export so component code doesn't
 * re-import from `coach_copy` for a bare number. */
export const FREE_TIER_TOTAL = COACH_COUNTER_TOTAL;


// ---------------------------------------------------------------------------
// G4D CP3 — Insert Suggestion + Undo helpers.
// Pure functions, unit-tested in `__tests__/insert_suggestion.test.js`.
// ---------------------------------------------------------------------------

/**
 * Banned-phrase vocabulary — must match backend
 * `modules/proposals/prompts/proposal_sections_v1.py::BANNED_PHRASES`
 * verbatim. Kept in-file to avoid a second export surface — the
 * test file re-declares this list and asserts alignment.
 */
const INSERT_BANNED_PHRASES = [
  "guaranteed",
  "guaranteed to win",
  "will get funded",
  "will receive",
  "definitely qualify",
  "sure to be awarded",
  "guarantee funding",
  "guarantee approval",
  "we promise",
  "you qualify",
  "you will receive",
  "coming soon",
  "beta",
  "placeholder",
  "we'll fix",
  "we can remove",
  "approved for",
  "definitely",
  "certainly get",
  "risk-free",
  "lorem",
  "awarded to you",
  "you're approved",
  "you are approved",
];

/** True when the suggestion text contains a banned phrase. */
export function hasBannedPhrase(text) {
  if (!text || typeof text !== "string") return false;
  const lower = text.toLowerCase();
  for (const phrase of INSERT_BANNED_PHRASES) {
    if (lower.includes(phrase)) return true;
  }
  return false;
}

/** True when the caller may click the Insert-Suggestion chip on this
 * action. Free-tier ALWAYS returns false regardless of suggestion
 * presence — the button stays disabled with the "Available in next
 * release" tooltip on free (per user brief). */
export function canInsertOnActionForTier(action, isFreeTier) {
  if (!action) return false;
  if (action.action_type !== "insert_suggestion") return false;
  if (!action.structured_suggestion) return false;
  if (isFreeTier) return false;
  return true;
}

/** Pure preview composition. Mirrors backend
 * `insert_action.apply_insert` semantics — append glues two
 * newlines when there's existing content; replace substitutes
 * entirely. Unknown modes return the current content unchanged so
 * the UI can't crash on typos. */
export function buildInsertPreview(current, suggestion, mode) {
  const cur = current || "";
  const sug = suggestion || "";
  if (mode === "append") {
    if (!cur) return sug;
    return cur + "\n\n" + sug;
  }
  if (mode === "replace") return sug;
  return cur;
}

/**
 * Compute a sha256 hex digest of the given string using the browser's
 * SubtleCrypto. Async. Returns a 64-char lowercase hex string.
 * The backend's `confirm_hash` uses the identical UTF-8 sha256 recipe.
 */
export async function computeSha256Hex(input) {
  const enc = new TextEncoder();
  const buf = enc.encode(input || "");
  const digest = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(digest);
  let out = "";
  for (const b of bytes) {
    out += b.toString(16).padStart(2, "0");
  }
  return out;
}

/** Alias for readability in the InsertSuggestionModal. */
export const computeInsertConfirmHash = computeSha256Hex;

/** Seconds remaining until the undo window closes. Returns 0 for
 * past deadlines and for null / undefined inputs. */
export function undoRemainingSeconds(undoExpiresAtIso) {
  if (!undoExpiresAtIso) return 0;
  const deadline = new Date(undoExpiresAtIso).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

/** True for terminal countdown states (no more ticking). */
export function isTerminalUndoStatus(status) {
  return status === "confirmed" || status === "expired" || status === "error";
}
